/**
 * Shared decode-then-resample PCM helper (cue-forge-sdv.11.6 final-review
 * cleanup; moved to the main thread by cue-forge-5nj): decode compressed
 * audio bytes to a single channel of PCM at `targetRate`. Decoding happens at
 * the file's native rate, then an OfflineAudioContext render
 * resamples/downmixes to mono at the target rate. Extracted from three
 * identical copies, originally in `resync.worker.ts`, `waveform.worker.ts`,
 * and `transcribe.worker.ts` themselves.
 *
 * Memory note (cue-forge-8oh finding 4 — corrected phrasing): the *render*
 * bounds the OUTPUT array's size (mono, `targetRate`), not the decode step.
 * `decodeAudioData`'s intermediate `AudioBuffer` is still float32 at the
 * source's native sample rate and channel count (≈21 MB/min for 44.1 kHz
 * stereo) — that intermediate, not this function's output, is what actually
 * limits how long a file can be decoded before the tab runs out of memory.
 *
 * cue-forge-5nj: `OfflineAudioContext` does not exist in dedicated Web
 * Workers on current browsers (verified empirically — Web Audio is
 * Window-only), so every caller now runs this on the MAIN thread: the
 * transcribe/resync/waveform features' `workerRunner.ts` files call this
 * before spawning their worker, then transfer the resulting PCM to it. Still
 * unavailable in Node/jsdom (tests stub `OfflineAudioContext` globally or
 * exercise the resulting rejection instead).
 */
export async function decodeToMono(audio: ArrayBuffer, targetRate: number): Promise<Float32Array> {
  const decodeCtx = new OfflineAudioContext(1, 1, 44100);
  const buffer = await decodeCtx.decodeAudioData(audio);

  const frames = Math.max(1, Math.ceil(buffer.duration * targetRate));
  const resampleCtx = new OfflineAudioContext(1, frames, targetRate);
  const source = resampleCtx.createBufferSource();
  source.buffer = buffer;
  source.connect(resampleCtx.destination);
  source.start();
  const rendered = await resampleCtx.startRendering();
  return rendered.getChannelData(0);
}
